import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface WaitlistRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventTitle: string;
  eventDate: Date;
  venue: string;
}

const WaitlistRequestDialog = ({
  open,
  onOpenChange,
  eventId,
  eventTitle,
  eventDate,
  venue,
}: WaitlistRequestDialogProps) => {
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("salon_waitlist")
        .delete()
        .eq("user_id", user.id)
        .eq("event_id", eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salon-waitlist", eventId] });
      toast({
        title: "Request cancelled",
        description: "Your waitlist request has been cancelled.",
      });
      onOpenChange(false);
    },
    onError: (error) => {
      console.error("Error cancelling request:", error);
      toast({
        title: "Error",
        description: "Failed to cancel request. Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">
            Request Submitted ⏳
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-lg font-medium text-primary">{eventTitle}</p>
              <div className="text-sm text-muted-foreground mt-1">
                <p>{format(eventDate, "EEEE, MMMM d, yyyy")}</p>
                <p>{format(eventDate, "h:mm a")}</p>
                <p className="mt-1">{venue}</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground px-4 text-center">
            We'll allocate spots at the salon in waves to balance between new and existing members, as well as diverse perspectives.
          </p>
          <div className="flex justify-center pt-2">
            <Button
              variant="destructive"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? "Cancelling..." : "Cancel Request"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistRequestDialog;
